import React, { useState, useEffect, useRef } from 'react';
import {
  easings,
  useReducedMotion,
  useSpring,
  useSpringValue,
} from '@react-spring/web';

import { useContentfulInspectorMode } from '@contentful/live-preview/react';

import { Container, Row, Section } from 'src/components-v2/Layout';
import useIntersection from 'src/hooks/useIntersection/useIntersection';
import useIsPotato from 'src/hooks/useIsPotato';
import { useTrack } from 'src/contexts/AnalyticsContext';
import { Events } from 'src/lib/trackingEvents';
import { isAutomatedBrowser } from 'src/lib/browser-utils';
import { MortgageCard } from './MortgageCard';
import { MortgageForm } from './MortgageForm';
import type { ICalculation, IUserInput } from './MortgageCalculator.types';
import { calculate } from './MortgageCalculator.utils';
import { resizeUnfilledSlider } from '../Calculator/Calculator.utils';
import Column from 'src/components-v2/Layout/Grid/Column';
import Stack from 'src/components-v2/Layout/Stack/Stack';

const CalcDefaults = {
  annualMortgageApplications: 6000,
  currentVoAConversionRate: 40,
  averageVerificationCostPerApplication: 200,
};

export const MortgageCalculator = ({ fields, sys }) => {
  const track = useTrack();
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  const isPotato = useIsPotato();
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useIntersection({
    element: sectionRef,
    threshold: 0.01,
    useOnce: true,
  });
  const prefersReducedMotion = useReducedMotion();

  // Render the settled defaults on first paint instead of the onload slide-in,
  // so Happo can't snapshot the calculator mid-animation (flaky 0 → values).
  const settleImmediately =
    isPotato || prefersReducedMotion || isAutomatedBrowser();

  // Check if formBody contains an h1 node
  const hasH1 = fields?.formBody?.content?.some((node) => {
    return node.nodeType === 'heading-1';
  });

  // app state
  const [userInput, setUserInput] = useState<IUserInput>(
    settleImmediately
      ? { ...CalcDefaults }
      : {
          annualMortgageApplications: 0,
          currentVoAConversionRate: 0,
          averageVerificationCostPerApplication: 0,
        },
  );
  const [calcState, setCalcState] = useState<ICalculation>(
    calculate(userInput),
  );
  const [hasError, setHasError] = useState(false);

  // refs
  const annualMortgageApplicationsInputRef = useRef<HTMLInputElement>(null);
  const annualMortgageApplicationsSliderRef = useRef<HTMLInputElement>(null);
  const currentVoAConversionRateInputRef = useRef<HTMLInputElement>(null);
  const currentVoAConversionRateSliderRef = useRef<HTMLInputElement>(null);
  const averageVerificationCostPerApplicationInputRef =
    useRef<HTMLInputElement>(null);
  const averageVerificationCostPerApplicationSliderRef =
    useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const hasUserInteracted = useRef(false);
  const hasAnimated = useRef(false);

  // animation values
  const annualMortgageApplicationsSliderValue = useSpringValue(
    settleImmediately ? CalcDefaults.annualMortgageApplications : 0,
    {
      config: {
        duration: 1000,
        easing: easings.easeInOutBack,
      },
    },
  );
  const currentVoAConversionRateSliderValue = useSpringValue(
    settleImmediately ? CalcDefaults.currentVoAConversionRate : 0,
    {
      config: {
        duration: 1000,
        easing: easings.easeInOutBack,
      },
    },
  );
  const averageVerificationCostPerApplicationSliderValue = useSpringValue(
    settleImmediately ? CalcDefaults.averageVerificationCostPerApplication : 0,
    {
      config: {
        duration: 1000,
        easing: easings.easeInOutBack,
      },
    },
  );

  const errorSpring = useSpring({
    opacity: hasError ? 0 : 1,
    x: hasError ? 16 : 0,
    config: { duration: 280 },
  });

  const errorMessageSpring = useSpring({
    opacity: hasError ? 1 : 0,
    x: hasError ? 0 : -24,
    delay: hasError ? 280 : 0,
    config: { duration: 280 },
  });

  const spotImageSpring = useSpring({
    opacity: hasError ? 1 : 0,
    x: hasError ? 0 : 24,
    delay: hasError ? 280 : 0,
    config: { duration: 280 },
  });

  // updates the the width of the unfilled part of the slider
  useEffect(() => {
    if (!currentVoAConversionRateSliderRef?.current) return;
    if (!averageVerificationCostPerApplicationSliderRef?.current) return;
    if (!annualMortgageApplicationsSliderRef?.current) return;
    resizeUnfilledSlider({ ref: currentVoAConversionRateSliderRef });
    resizeUnfilledSlider({
      ref: averageVerificationCostPerApplicationSliderRef,
    });
    resizeUnfilledSlider({ ref: annualMortgageApplicationsSliderRef });
  }, [
    userInput.currentVoAConversionRate,
    userInput.averageVerificationCostPerApplication,
    userInput.annualMortgageApplications,
  ]);

  // recalculate on change
  useEffect(() => {
    setCalcState(calculate(userInput));
  }, [userInput]);

  useEffect(() => {
    if (calcState.savings < 0 && !hasError) {
      setHasError(true);
    }

    if (calcState.savings >= 0 && hasError) {
      setHasError(false);
    }
  }, [calcState.savings, hasError]);

  // Run onload animation and track VIEWED only when component is in view
  useEffect(() => {
    if (!isInView) return;

    // Already settled (see `settleImmediately`) — skip the slide-in, just track the view.
    if (settleImmediately) {
      track({ type: Events.VIEWED, payload: { ...fields?.trackingContext } });
      return;
    }

    // Reset so each mount (e.g. after React Strict Mode remount) can run the animation
    hasAnimated.current = false;

    let cancelled = false;
    let timeoutId: number | null = null;

    const tryStartAnimations = () => {
      if (cancelled) return;
      if (
        !annualMortgageApplicationsSliderRef.current ||
        !currentVoAConversionRateSliderRef.current ||
        !averageVerificationCostPerApplicationSliderRef.current
      ) {
        return false;
      }

      hasAnimated.current = true;

      annualMortgageApplicationsSliderValue.start({
        to: CalcDefaults.annualMortgageApplications,
        onChange: () => {
          resizeUnfilledSlider({ ref: annualMortgageApplicationsSliderRef });
        },
        onRest: () => {
          setUserInput((prev) => {
            return {
              ...prev,
              annualMortgageApplications:
                CalcDefaults.annualMortgageApplications,
            };
          });
        },
      });

      currentVoAConversionRateSliderValue.start({
        to: CalcDefaults.currentVoAConversionRate,
        onChange: () => {
          resizeUnfilledSlider({ ref: currentVoAConversionRateSliderRef });
        },
        onRest: () => {
          setUserInput((prev) => {
            return {
              ...prev,
              currentVoAConversionRate: CalcDefaults.currentVoAConversionRate,
            };
          });
        },
      });

      averageVerificationCostPerApplicationSliderValue.start({
        to: CalcDefaults.averageVerificationCostPerApplication,
        onChange: () => {
          resizeUnfilledSlider({
            ref: averageVerificationCostPerApplicationSliderRef,
          });
        },
        onRest: () => {
          setUserInput((prev) => {
            return {
              ...prev,
              averageVerificationCostPerApplication:
                CalcDefaults.averageVerificationCostPerApplication,
            };
          });
        },
      });

      track({
        type: Events.VIEWED,
        payload: {
          ...fields?.trackingContext,
        },
      });
      return true;
    };

    // Wait for layout (two frames) then a short delay so refs are attached; retry if refs not ready
    const scheduleTry = (attempt = 0) => {
      if (cancelled) return;
      const maxAttempts = 20;
      const delay = attempt === 0 ? 150 : 50;

      timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        if (tryStartAnimations()) return;
        if (attempt < maxAttempts - 1) {
          scheduleTry(attempt + 1);
        }
      }, delay);
    };

    const frameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scheduleTry(0);
      });
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
      if (timeoutId != null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    isInView,
    settleImmediately,
    track,
    fields?.trackingContext,
    annualMortgageApplicationsSliderValue,
    currentVoAConversionRateSliderValue,
    averageVerificationCostPerApplicationSliderValue,
  ]);

  const setHasUserInteracted = () => {
    if (!hasUserInteracted?.current) {
      hasUserInteracted.current = true;
      track({
        type: Events.CHANGED,
        payload: {
          ...fields?.trackingContext,
        },
      });
    }
  };

  const handleChange = ({ target: { name, value } }) => {
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    setUserInput((prevState) => {
      return {
        ...prevState,
        [name]: sanitizedValue,
      };
    });
    setHasUserInteracted();
  };

  return (
    <Section
      ref={sectionRef}
      component={hasH1 ? 'header' : 'section'}
      className='cms-mortgage-calculator'
      {...inspectorProps({ fieldId: 'internalTitle' })}
      sx={fields?.styles}
    >
      <Container className='cms-mortgage-calculator-container'>
        <Row className='cms-mortgage-calculator-row'>
          <Column
            className='cms-mortgage-calculator-column'
            xsOffset={1}
            xs={22}
            smOffset={0}
            sm={24}
            lgOffset={2}
            lg={20}
          >
            <Stack
              className='cms-mortgage-calculator-stack'
              direction={{ xs: 'column', md: 'row' }}
              gap={{ xs: 5.25, sm: 9.125, lg: 17.5 }}
            >
              <MortgageForm
                annualMortgageApplicationsInputRef={
                  annualMortgageApplicationsInputRef
                }
                annualMortgageApplicationsSliderRef={
                  annualMortgageApplicationsSliderRef
                }
                currentVoAConversionRateInputRef={
                  currentVoAConversionRateInputRef
                }
                currentVoAConversionRateSliderRef={
                  currentVoAConversionRateSliderRef
                }
                averageVerificationCostPerApplicationInputRef={
                  averageVerificationCostPerApplicationInputRef
                }
                averageVerificationCostPerApplicationSliderRef={
                  averageVerificationCostPerApplicationSliderRef
                }
                fields={fields}
                formRef={formRef}
                handleChange={handleChange}
                hasUserInteracted={hasUserInteracted}
                sys={sys}
                userInput={userInput}
                annualMortgageApplicationsSliderValue={
                  annualMortgageApplicationsSliderValue
                }
                currentVoAConversionRateSliderValue={
                  currentVoAConversionRateSliderValue
                }
                averageVerificationCostPerApplicationSliderValue={
                  averageVerificationCostPerApplicationSliderValue
                }
              />
              <MortgageCard
                calcState={calcState}
                fields={fields}
                inspectorProps={inspectorProps}
                isPotato={isPotato}
                sys={sys}
                hasError={hasError}
                errorSpring={errorSpring}
                errorMessageSpring={errorMessageSpring}
                spotImageSpring={spotImageSpring}
              />
            </Stack>
          </Column>
        </Row>
      </Container>
    </Section>
  );
};
